import type { LocationQueryValue } from 'vue-router';
import { IntroductionSubjectEnum } from '#base/core/enums/IntroductionSubjectEnum';
import type { PgtSubjectEnum } from '#base/core/enums/PgtSubjectEnum';
import type { DialogNameType } from '#base/core/types/DialogNameType';

export const DIALOG_COOKIE = 'dialog-cookie';
export const DIALOG_APPOINTMENT = 'dialog-appointment';
export const DIALOG_PGT_INFO = 'dialog-pgt-info';
export const DIALOG_PT_INFO = 'dialog-pt-info';
export const DIALOG_INTRODUCTION = 'dialog-introduction';
export const DIALOG_CONTACT = 'dialog-contact';
export const DIALOG_MOBILE_MENU = 'dialog-mobile-menu';

const APPOINTMENT_QUERY = 'afspraak-inplannen';
export const PGT_INTRODUCTION_QUERY = 'kennismaking-pgt';

const hideLogo = ref<boolean>(false);
const hideRegularCloseButton = ref<boolean>(false);
const openDialog = ref<string>();

const dialogPgtInfoProps = reactive<{ subject?: PgtSubjectEnum }>({
	subject: undefined,
});

const dialogIntroductionProps = reactive<{ subject?: IntroductionSubjectEnum }>({
	subject: undefined,
});

export default () => {
	const route = useRoute();
	const routeQuery = computed(() => route.query.d as LocationQueryValue);

	const handleDialogQuery = (queryValue: LocationQueryValue) => {
		const dialog:
			| DialogNameType
			| LocationQueryValue
			| LocationQueryValue[] = queryValue;

		if (!dialog) {
			openDialog.value = undefined;

			return;
		}

		if (dialog === APPOINTMENT_QUERY) {
			openDialog.value = DIALOG_APPOINTMENT;
		}

		if (dialog === PGT_INTRODUCTION_QUERY) {
			openDialog.value = DIALOG_INTRODUCTION;
			dialogIntroductionProps.subject = IntroductionSubjectEnum.PGT;
		}

		if ([DIALOG_PGT_INFO, DIALOG_PT_INFO].includes(dialog)) {
			openDialog.value = dialog;
		}
	};

	onMounted(() => {
		if (routeQuery.value) {
			handleDialogQuery(routeQuery.value);
		}
	});

	watch(routeQuery, (routeQuery) => {
		handleDialogQuery(routeQuery);
	});

	const openCookieDialog = () => {
		openDialog.value = DIALOG_COOKIE;
	};

	const openAppointmentDialog = () => {
		openDialog.value = DIALOG_APPOINTMENT;
		navigateTo({
			query: {
				...route.query,
				d: APPOINTMENT_QUERY,
			},
		});
	};

	const openPgtInfoDialog = (subject?: PgtSubjectEnum) => {
		openDialog.value = DIALOG_PGT_INFO;
		dialogPgtInfoProps.subject = subject;
	};

	const openPtInfoDialog = (subject?: PgtSubjectEnum) => {
		openDialog.value = DIALOG_PT_INFO;
		dialogPgtInfoProps.subject = subject;
	};

	const openIntroductionDialog = (subject?: IntroductionSubjectEnum) => {
		openDialog.value = DIALOG_INTRODUCTION;
		dialogIntroductionProps.subject = subject;
	};

	const openContactDialog = () => {
		openDialog.value = DIALOG_CONTACT;
	};

	const openMobileMenu = () => {
		openDialog.value = DIALOG_MOBILE_MENU;
	};

	const closeMobileMenu = () => {
		openDialog.value = undefined;
	};

	const closeDialog = () => {
		openDialog.value = undefined;
		navigateTo({
			query: {
				...route.query,
				d: undefined,
			},
		});

		setHideLogo(false);
		setHideRegularCloseButton(false);
	};

	const setHideRegularCloseButton = (value: boolean) => {
		hideRegularCloseButton.value = value;
	};

	const setHideLogo = (value: boolean) => {
		hideLogo.value = value;
	};

	return {
		isCookieDialogOpen: computed(() => openDialog.value === DIALOG_COOKIE),
		isApplyDialogOpen: computed(
			() => openDialog.value === DIALOG_APPOINTMENT
		),
		isPgtInfoDialogOpen: computed(
			() => openDialog.value === DIALOG_PGT_INFO
		),
		isPtInfoDialogOpen: computed(
			() => openDialog.value === DIALOG_PT_INFO
		),
		isIntroductionDialogOpen: computed(
			() => openDialog.value === DIALOG_INTRODUCTION
		),
		isContactDialogOpen: computed(
			() => openDialog.value === DIALOG_CONTACT
		),
		isMobileMenuOpen: computed(
			() => openDialog.value === DIALOG_MOBILE_MENU
		),
		isDialogOpen: computed(() => openDialog.value !== undefined),
		isDialogBottom: computed(() => {
			switch (openDialog.value) {
				case DIALOG_COOKIE:
					return true;
				default:
					return false;
			}
		}),
		closeDialog,
		openCookieDialog,
		openAppointmentDialog,
		openPgtInfoDialog,
		openPtInfoDialog,
		openIntroductionDialog,
		openContactDialog,
		openMobileMenu,
		closeMobileMenu,
		hideRegularCloseButton,
		setHideRegularCloseButton,
		hideLogo,
		setHideLogo,
		dialogPgtInfoProps,
		dialogIntroductionProps,
	};
};
